import React from "react";
import { useEffect, useRef, useState } from "react";
import { theme } from "../../constants/StyledComponents";
import { FontWeightEnum } from "../../types/AppTypes";
import { BodyTyp } from "../Typography/Typography";
import {
  StyledTabBookmark,
  StyledTabSwitch,
  StyledTabSwitchContainer,
  StyledTabSwitchRow,
} from "./TabSwitch.styled";

export type TabSwitchLabel = {
  label: string;
  count?: number;
};

export type TabSwitchItem<T> = {
  value: T;
  item: TabSwitchLabel;
};

type TabSwitchProps<T> = {
  children?: React.ReactNode;
  tabs: TabSwitchItem<T>[];
  selectedTab: TabSwitchItem<T>;
  setSelectedTab: (x: TabSwitchItem<T>) => void;
};
export const TabSwitch = <T,>({
  children,
  tabs,
  selectedTab,
  setSelectedTab,
}: TabSwitchProps<T>) => {
  const [bookmarkStyle, setBookmarkStyle] = useState({ width: 0, left: 0 });
  const [allowTransitions, setAllowTransitions] = useState(false);

  const isActive = (tab: TabSwitchItem<T>) => {
    return selectedTab.value === tab.value;
  };

  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const activeIndex = tabs.findIndex(
      (tab) => tab.value === selectedTab.value,
    );
    const activeTab = tabRefs.current[activeIndex];

    if (activeTab) {
      const rect = activeTab.getBoundingClientRect();
      const parentRect = activeTab.parentElement?.getBoundingClientRect();
      setBookmarkStyle({
        width: rect.width,
        left: parentRect ? rect.left - parentRect.left : rect.left,
      });
      setTimeout(() => setAllowTransitions(true), 0);
    }
  }, [tabs, selectedTab]);

  return (
    <StyledTabSwitchContainer>
      <StyledTabSwitchRow>
        {tabs.map((tab, index) => {
          return (
            <StyledTabSwitch
              ref={(el) => (tabRefs.current[index] = el)}
              $isActive={isActive(tab)}
              key={`tab-${tab.value}`}
              onClick={() => setSelectedTab(tab)}
            >
              <BodyTyp
                color={
                  isActive(tab) ? theme.colors.purple : theme.colors.grey_2
                }
                variant="3"
                fontWeight={
                  isActive(tab) ? FontWeightEnum.MEDIUM : FontWeightEnum.REGULAR
                }
              >
                {tab.item.label}
              </BodyTyp>
              {tab.item.count !== undefined && (
                <div className="tab-counter">
                  <BodyTyp
                    color={
                      isActive(tab) ? theme.colors.purple : theme.colors.grey_2
                    }
                    variant="3"
                    fontWeight={
                      isActive(tab)
                        ? FontWeightEnum.MEDIUM
                        : FontWeightEnum.REGULAR
                    }
                  >
                    {tab.item.count < 999 ? tab.item.count : "999+"}
                  </BodyTyp>
                </div>
              )}
            </StyledTabSwitch>
          );
        })}
      </StyledTabSwitchRow>
      <StyledTabBookmark
        $width={bookmarkStyle.width}
        $left={bookmarkStyle.left}
        $allowTransitions={allowTransitions}
      />
      {children && <div className="tabs-content">{children}</div>}
    </StyledTabSwitchContainer>
  );
};
